import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";

const HEADER = "https://static.prod-images.emergentagent.com/jobs/1f9c6200-ca73-4c9b-a307-361cf054655d/images/4066b8744659f32e1a25aab4a5f9da4ddfffb825eb901bf76dae7e549b492503.png";

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo title="Services" description="Three engagement models — Retained Search, Contingency Search, and RPO — for senior construction and infrastructure hiring across the UK, Europe and the Middle East." path="/services" />

      <section className="relative h-[60vh] min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HEADER})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1530]/95 via-[#0E1530]/50 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pb-16 w-full">
          <div className="overline-light">Services</div>
          <h1 className="font-serif text-white text-5xl sm:text-7xl mt-6 max-w-3xl leading-[0.95]" data-testid="services-headline">
            Three engagement models. One outcome.
          </h1>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline">Engagement models</div>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] mt-6">Choose the model that fits the mandate.</h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <p className="text-lg leading-relaxed text-[#222]">
              Different roles demand different approaches. B7 offers three distinct engagement
              models — from fully retained executive search to embedded multi-hire programmes —
              each designed to balance speed, depth and commercial certainty.
            </p>
          </div>
        </div>
      </section>

      {/* 01 RETAINED — featured */}
      <section className="bg-[#0E1530] text-white" data-testid="service-retained">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline-light">01 — Recommended</div>
            <h2 className="font-serif text-4xl sm:text-6xl mt-6 leading-[0.95]">Retained Search</h2>
            <p className="mt-3 inline-flex items-center gap-2 text-sm tracking-[0.18em] uppercase font-semibold text-[#2EA3F2]">
              Our highest-success engagement
            </p>
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg leading-relaxed text-white/85">
              An exclusive, dedicated mandate where B7 commits the full weight of the network
              to a single role. Suited to senior, business-critical or commercially sensitive
              hires where the cost of the wrong appointment far exceeds the cost of the search.
            </p>

            <div className="mt-8">
              <div className="overline-light">Why retained converts most consistently</div>
              <ul className="mt-4 space-y-3">
                {[
                  "Exclusivity unlocks deep market mapping — we approach the entire passive market, not just active candidates.",
                  "A dedicated consultant team commits scheduled hours each week to your role.",
                  "Structured milestones (longlist → shortlist → interview → offer) with full transparency on progress.",
                  "Higher offer-acceptance and lower drop-out: candidates engage with a single, focused process.",
                  "Genuine partnership pricing — phased fee structure tied to delivery milestones, not just placement.",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-white/85">
                    <Check size={18} className="text-[#2EA3F2] shrink-0 mt-1" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-white/10 pt-6">
              <div>
                <div className="overline-light">Best for</div>
                <p className="text-sm text-white/75 mt-2">Director-level &amp; above, niche specialists, confidential mandates.</p>
              </div>
              <div>
                <div className="overline-light">Typical timeline</div>
                <p className="text-sm text-white/75 mt-2">Shortlist within 3–4 weeks.</p>
              </div>
              <div>
                <div className="overline-light">Commitment</div>
                <p className="text-sm text-white/75 mt-2">Exclusive engagement, phased fee.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 02 CONTINGENCY */}
      <section className="border-b border-[#E2E0D9]" data-testid="service-contingency">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">02</div>
            <h2 className="font-serif text-4xl sm:text-6xl mt-6 leading-[0.95]">Contingency Search</h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg leading-relaxed text-[#222]">
              A non-exclusive, pay-on-placement model. B7 sources candidates and only invoices
              when a hire is made. Useful where speed-to-CV is the priority and the role is
              well-defined and active in the market.
            </p>

            <div className="mt-8">
              <div className="overline">When contingency is the right call</div>
              <ul className="mt-4 space-y-3">
                {[
                  "Mid-level technical hires where a strong active candidate pool already exists.",
                  "Volume hiring on lower-criticality positions where you want to test the market.",
                  "Roles already covered by an internal team — B7 acts as an additional channel.",
                  "Short-notice contract / day-rate placements.",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-[#222]">
                    <Check size={18} className="text-[#1E2A78] shrink-0 mt-1" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-[#E2E0D9] pt-6">
              <div>
                <div className="overline">Best for</div>
                <p className="text-sm text-[#5C5C5C] mt-2">Mid-level technical &amp; commercial roles.</p>
              </div>
              <div>
                <div className="overline">Typical timeline</div>
                <p className="text-sm text-[#5C5C5C] mt-2">CVs within days; placement on acceptance.</p>
              </div>
              <div>
                <div className="overline">Commitment</div>
                <p className="text-sm text-[#5C5C5C] mt-2">Non-exclusive, no upfront fee.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 03 RPO */}
      <section data-testid="service-rpo">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">03</div>
            <h2 className="font-serif text-4xl sm:text-6xl mt-6 leading-[0.95]">RPO</h2>
            <p className="mt-3 text-sm tracking-[0.18em] uppercase font-semibold text-[#5C5C5C]">
              Recruitment Process Outsourcing
            </p>
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg leading-relaxed text-[#222]">
              A managed, embedded service for clients hiring multiple roles across a fixed
              programme or mobilisation window. B7 operates as an extension of your in-house
              talent team — owning the full lifecycle from sourcing to onboarding under a
              transparent fixed-fee or per-hire commercial structure.
            </p>

            <div className="mt-8">
              <div className="overline">Where RPO pays back</div>
              <ul className="mt-4 space-y-3">
                {[
                  "Project mobilisations requiring 10+ hires inside a defined window.",
                  "Significantly lower cost-per-hire vs. multiple contingency placements.",
                  "Predictable, fixed monthly run-rate replaces variable placement fees.",
                  "Branded candidate experience — you control comms; we drive the funnel.",
                  "Single point of accountability for talent metrics, reporting and SLAs.",
                  "Smooth wind-down at programme end — capability transferred back in-house.",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-[#222]">
                    <Check size={18} className="text-[#1E2A78] shrink-0 mt-1" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-[#E2E0D9] pt-6">
              <div>
                <div className="overline">Best for</div>
                <p className="text-sm text-[#5C5C5C] mt-2">Multi-hire mobilisations &amp; project ramp-ups.</p>
              </div>
              <div>
                <div className="overline">Typical timeline</div>
                <p className="text-sm text-[#5C5C5C] mt-2">3 – 12 month fixed engagements.</p>
              </div>
              <div>
                <div className="overline">Commitment</div>
                <p className="text-sm text-[#5C5C5C] mt-2">Embedded team, fixed commercial structure.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FOOT */}
      <section className="bg-[#F4F3F0] border-t border-[#E2E0D9]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="overline">Next step</div>
            <h3 className="font-serif text-3xl sm:text-4xl mt-3">Discuss the right model for your mandate.</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary" data-testid="services-contact-btn">
              Contact us <ArrowRight size={16} />
            </Link>
            <Link to="/submit-cv" className="btn-secondary" data-testid="services-submit-cv-btn">
              Submit CV
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
